<template>
  <div class="product-page">
    <ProductHeader 
      :totalCount="totalCount" 
      :productName="categoryInfo.CategoryName" 
      :productDescription="categoryInfo.Description" 
    />

    <!-- 第一组产品（前10个） -->
    <div class="product-grid">
      <!-- Large Lifestyle Image -->
      <div class="grid-item large-item">
        <ProgressiveImage :src="decorativeImageList[0] || categoryInfo.DecorativeImage || largeImg1" alt="Lifestyle" />
      </div>

      <!-- Top 6 products -->
      <div 
        class="grid-item product-item" 
        v-for="(item, index) in initialProducts.slice(0, 6)" 
        :key="'top-' + index"
      >
        <div 
          class="product-image-wrapper" 
          :class="{ 'origin-left': index === 2 || index === 3 }" 
          @mouseenter="handleHover(item, $event)" 
          @mouseleave="handleHover(null)" 
          @click="openDetail(item)"
        >
          <ProgressiveImage :src="item.MainImage" alt="Product" customClass="product-thumb" />
        </div>
      </div>

      <!-- Bottom 4 products -->
      <div 
        class="grid-item product-item" 
        v-for="(item, index) in initialProducts.slice(6, 10)" 
        :key="'bottom-' + index"
      >
        <div 
          class="product-image-wrapper" 
          :class="{ 'origin-left': index < 2 }" 
          @mouseenter="handleHover(item, $event)" 
          @mouseleave="handleHover(null)" 
          @click="openDetail(item)"
        >
          <ProgressiveImage :src="item.MainImage" alt="Product" customClass="product-thumb" />
        </div>
      </div>
    </div>

    <!-- 后续产品组（每10个一组） -->
    <div 
      class="product-grid" 
      v-for="(group, groupIndex) in additionalProductGroups" 
      :key="'group-' + groupIndex"
    >
      <!-- Large Lifestyle Image with alternate side -->
      <div 
        class="grid-item large-item" 
        :class="{ 'right-side': (groupIndex + 1) % 2 !== 0 }"
      >
        <ProgressiveImage :src="getDecorativeImageForGroup(groupIndex) || largeImg" alt="Lifestyle" />
      </div>

      <!-- Group Top 6 products -->
      <div 
        class="grid-item product-item" 
        v-for="(item, index) in group.slice(0, 6)"
        :key="'group-top-' + groupIndex + '-' + index"
      >
        <div 
          class="product-image-wrapper" 
          :class="{ 'origin-left': (groupIndex % 2 === 0 ? index < 4 : (index === 2 || index === 3)) }" 
          @mouseenter="handleHover(item, $event)" 
          @mouseleave="handleHover(null)" 
          @click="openDetail(item)"
        >
          <ProgressiveImage :src="item.MainImage" alt="Product" customClass="product-thumb" />
        </div>
      </div>

      <!-- Group Bottom 4 products -->
      <div 
        class="grid-item product-item" 
        v-for="(item, index) in group.slice(6, 10)"
        :key="'group-bottom-' + groupIndex + '-' + index"
      >
        <div 
          class="product-image-wrapper" 
          :class="{ 'origin-left': index < 2 }" 
          @mouseenter="handleHover(item, $event)" 
          @mouseleave="handleHover(null)" 
          @click="openDetail(item)"
        >
          <ProgressiveImage :src="item.MainImage" alt="Product" customClass="product-thumb" />
        </div>
      </div>
    </div>

    <!-- Footer组件 -->
    <Footer layout="about" :show-general-enquires="true" :show-studio="true" :show-instagram="true" />

    <!-- 产品预览图浮层 -->
    <transition name="preview-anim">
      <div v-if="hoveredProduct" class="preview-overlay" :style="previewOverlayStyle">
        <ProgressiveImage :src="hoveredProduct.hoverSrc" alt="Product Preview" />
        <div class="preview-name">{{ hoveredProduct.ProductName }}</div>
      </div>
    </transition>
  </div>
</template>

<script>
import ProductHeader from '@/components/productHeader.vue'
import Footer from '@/components/Footer.vue'
import ProgressiveImage from '@/components/ProgressiveImage.vue'
import { getCategoryDetail } from '@/api/product'

// 占位图片（当接口返回为空时使用）
const largeImg1 = require('@/assets/product/TGT-Melissa-Marshall-27 1.jpg')
const largeImg = require('@/assets/_DSC0544 2 3-1.png')
const charImage = require('@/assets/product/char.png')

export default {
  name: 'ProductPage',
  components: {
    ProductHeader,
    Footer,
    ProgressiveImage
  },
  data() {
    return {
      largeImg1,
      largeImg,
      charImage,
      categoryInfo: {},                 // 分类信息（包含 DecorativeImage 等）
      initialProducts: [],              // 第一组产品（前10个）
      additionalProductGroups: [],      // 后续产品组（每组10个）
      hoveredProduct: null,
      previewPosition: null,
      totalCount: 0,
    }
  },
  computed: {
    previewOverlayStyle() {
      return {
        left: `${this.previewPosition.x}rem`,
        top: `${this.previewPosition.y}rem`
      }
    },
    // 收集所有非空的装饰图（DecorativeImage ~ DecorativeImage8）
    decorativeImageList() {
      const images = []
      const keys = [
        'DecorativeImage',
        'DecorativeImage2',
        'DecorativeImage3',
        'DecorativeImage4',
        'DecorativeImage5',
        'DecorativeImage6',
        'DecorativeImage7',
        'DecorativeImage8'
      ]
      for (const key of keys) {
        const url = this.categoryInfo[key]
        if (url && typeof url === 'string' && url.trim() !== '') {
          images.push(url)
        }
      }
      return images
    }
  },
  watch: {
    '$route.query.CategoryID': {
      handler() {
        this.fetchCategoryDetail()
      },
      immediate: true
    }
  },
  methods: {
    // 从路由获取 CategoryID
    getCategoryIdFromRoute() {
      const raw = this.$route.query.CategoryID
      if (raw === undefined || raw === null) return null
      const parsed = Number(raw)
      return isNaN(parsed) ? null : parsed
    },

    // 获取分类详情及产品列表
    async fetchCategoryDetail() {
      // 清空旧数据，避免残留
      this.initialProducts = []
      this.additionalProductGroups = []
    
      const categoryId = this.getCategoryIdFromRoute()
      if (categoryId === null) return

      try {
        const response = await getCategoryDetail(categoryId)
        // 兼容两种返回格式：直接对象 或 嵌套在 result 中
        const data = response.result || response

        this.categoryInfo = data
        this.totalCount = data.ProductCount || 0
    
        const products = (data.Products || []).map(p => ({
          ...p,
          hoverSrc: p.GalleryImage || p.BigImage || p.MainImage || ''
        }))
    
        const groupSize = 10
        this.initialProducts = products.slice(0, groupSize)
        const remaining = products.slice(groupSize)
        for (let i = 0; i < remaining.length; i += groupSize) {
          this.additionalProductGroups.push(remaining.slice(i, i + groupSize))
        }
    
        if (categoryId === 0) {
          this.categoryInfo = {
            ...this.categoryInfo,
            CategoryName: 'ALL Products',
            Description: 'All Product Displays'
          }
        }
      } catch (error) {
        console.error('Failed to load products:', error)
      }
    },

    // 根据组索引获取对应的装饰图（第一组使用第1张，后续组依次取下一张，循环）
    getDecorativeImageForGroup(groupIndex) {
      const list = this.decorativeImageList
      if (list.length === 0) return null
      // 第一组（groupIndex = 0）已经单独使用了第1张（索引0），这里从第2张开始轮换
      // 由于后续组的 groupIndex 是从 0 开始，需要映射到列表索引 (groupIndex + 1) % list.length
      const idx = (groupIndex + 1) % list.length
      return list[idx]
    },

    // 处理鼠标悬停预览
    handleHover(item, event) {
      if (item) {
        this.hoveredProduct = item
        if (event.currentTarget) {
          // 修正默认字体大小：浏览器默认16px，避免降级为1导致位置计算错误
          const rootFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16
          const targetRect = event.currentTarget.getBoundingClientRect()
          const targetCenterX = targetRect.left + targetRect.width / 2
          const targetCenterY = targetRect.top + targetRect.height / 2

          const previewWidthRem = 494
          const previewHeightRem = 494
          const previewWidthPx = previewWidthRem * rootFontSize
          const previewHeightPx = previewHeightRem * rootFontSize

          const viewportWidth = window.innerWidth
          const viewportHeight = window.innerHeight

          let finalX = targetCenterX
          let finalY = targetCenterY

          const isTouchingRight = targetRect.right >= viewportWidth - 5
          const isTouchingLeft = targetRect.left <= 5

          if (isTouchingRight) {
            finalX = viewportWidth - previewWidthPx / 2
          } else if (isTouchingLeft) {
            finalX = previewWidthPx / 2
          } else {
            if (finalX + previewWidthPx / 2 > viewportWidth) {
              finalX = viewportWidth - previewWidthPx / 2
            }
            if (finalX - previewWidthPx / 2 < 0) {
              finalX = previewWidthPx / 2
            }
          }

          if (finalY + previewHeightPx / 2 > viewportHeight) {
            finalY = viewportHeight - previewHeightPx / 2
          }
          if (finalY - previewHeightPx / 2 < 0) {
            finalY = previewHeightPx / 2
          }

          this.previewPosition = {
            x: finalX / rootFontSize,
            y: finalY / rootFontSize
          }
        }
      } else {
        this.hoveredProduct = null
      }
    },

    // 跳转产品详情页
    openDetail(product) {
      this.$router.push({
        path: '/product/detail',
        query: { productId: product.ProductID }
      })
    },
  },
  beforeDestroy() {
    // 清理工作（如需要）
  }
}
</script>

<style scoped>
/* 以下样式保持原有，无任何改动 */
.product-page {
  width: 100%;
  min-height: 100vh;
  background-color: #fff;
  scrollbar-width: none;
  -ms-overflow-style: none;
  overflow-y: auto;
}
.product-page::-webkit-scrollbar {
  display: none;
}
.idle-character-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 9999;
  overflow: hidden;
}
.idle-character {
  position: absolute;
  width: 150rem;
  height: auto;
  opacity: 0;
  animation: idle-walk 15s linear forwards;
}
@keyframes idle-walk {
  0% {
    top: 16vh;
    left: -200rem;
    opacity: 1;
    transform: translateY(-50%) scaleX(1);
  }
  33% {
    top: 16vh;
    left: 100vw;
    opacity: 1;
    transform: translateY(-50%) scaleX(1);
  }
  33.01% {
    top: 50vh;
    left: 100vw;
    opacity: 0;
    transform: translateY(-50%) scaleX(1);
  }
  33.02% {
    top: 50vh;
    left: 100vw;
    opacity: 1;
    transform: translateY(-50%) scaleX(1);
  }
  66% {
    top: 50vh;
    left: -200rem;
    opacity: 1;
    transform: translateY(-50%) scaleX(1);
  }
  66.01% {
    top: 83vh;
    left: -200rem;
    opacity: 0;
    transform: translateY(-50%) scaleX(1);
  }
  66.02% {
    top: 83vh;
    left: -200rem;
    opacity: 1;
    transform: translateY(-50%) scaleX(1);
  }
  99% {
    top: 83vh;
    left: 100vw;
    opacity: 1;
    transform: translateY(-50%) scaleX(1);
  }
  100% {
    top: 83vh;
    left: 100vw;
    opacity: 0;
    transform: translateY(-50%) scaleX(1);
  }
}
.product-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
}
.grid-item {
  position: relative;
  overflow: hidden;
  width: 100%;
}
.grid-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.large-item {
  grid-column: span 2;
  grid-row: span 3;
  width: 100%;
  height: 1134rem;
  background-color: #f5f5f5;
}
.product-item {
  grid-column: span 1;
  grid-row: span 1;
  background-color: #fafafa;
  position: relative;
  overflow: hidden;
  height: 378rem;
}
.product-image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
  transform-origin: right center;
}
.product-image-wrapper.origin-left {
  transform-origin: left center;
}
.product-image-wrapper:hover {
  background-color: #f0f0f0;
}
@media (min-width: 1025px) {
  .large-item.right-side {
    grid-column: 3 / span 2;
    grid-row: 1 / span 3;
  }
}
@media (max-width: 1024px) {
  .product-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .large-item {
    grid-column: span 2;
    grid-row: auto;
    height: auto;
    aspect-ratio: 16/9;
  }
  .product-item {
    grid-column: span 1;
    height: auto;
    aspect-ratio: 1;
  }
}
@media (max-width: 768px) {
  .sidebar-wrap {
    width: 100%;
  }
  .product-grid {
    grid-template-columns: 1fr;
  }
  .large-item {
    grid-column: span 1;
    aspect-ratio: 4/3;
  }
  .product-item {
    grid-column: span 1;
    height: auto;
    aspect-ratio: 1;
  }
}
.loading-indicator {
  width: 100%;
  text-align: center;
  padding: 20rem;
  font-size: 14rem;
  color: #666;
  background-color: #fff;
}
.preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  transform: translate(-50%, -50%) scale(1);
  width: 494rem;
  height: 494rem;
  z-index: 1000;
  pointer-events: none;
  overflow: hidden;
  background-color: #fff;
}
.preview-overlay img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 1rem solid #000;
}
.preview-name {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-family: 'Apercu-Medium', sans-serif;
  font-size: 24rem;
  font-weight: 500;
  text-align: center;
  pointer-events: none;
  z-index: 2;
  line-height: 1.4;
  padding: 20rem;
  box-sizing: border-box;
  word-break: break-word;
}
.preview-anim-enter-active,
.preview-anim-leave-active {
  transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
.preview-anim-enter,
.preview-anim-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0);
}
</style>